import { MainApi } from 'api/MainApi'
import Box from 'components/Box/Box'
import { Button } from 'components/Button/Button'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import Loader from 'components/Loader/Loader'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import ScenarioTable from 'components/ScenarioTable/ScenarioTable'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import Title from 'components/Title/Title'
import { Feature } from 'geojson'
import CleaningPointsOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningPointsOnMap'
import CleaningRoutesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningRoutesOnMap'
import CleaningZonesNamesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningZonesNamesOnMap'
import CleaningZonesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningZonesOnMap'
import MissionStartFinishPoints from 'pages/Mission/MissionView/MissionMap/Layers/MissionStartFinishPoints'
import RestrictZonesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/RestrictZonesOnMap'
import TransferRoutesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/TransferRoutesOnMap'
import TaskSettingsModal from 'pages/TaskScheduler/TaskSettingsModal/TaskSettingsModal'
import GausiumMapPreviewOverlay from 'components/GausiumMapPreviewOverlay/GausiumMapPreviewOverlay'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MapboxMap } from 'react-map-gl'
import { useNavigate } from 'react-router-dom'
import { GAUSIUM_ENABLED, PATH, PROGRESS_TYPES } from 'utils/constants'
import { IMapboxElement, IMission } from 'utils/interfaces'
import { zoomMapTo } from 'utils/mapUtils'

import style from './ScenarioList.module.css'

const ScenarioList = () => {
  const navigate = useNavigate()
  const [scenarios, setScenarios] = useState<any[]>([])
  const [loadingProgress, setLoadingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [listRefreshing, setListRefreshing] = useState(false)
  const loadingProgressRef = useRef(PROGRESS_TYPES.IDLE)
  const [creatingProgress, setCreatingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageInput, setPageInput] = useState('1')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [totalCount, setTotalCount] = useState(0)
  const lastFetchedPage = useRef<string | null>(null)
  const PAGE_SIZE = 20
  const [vendorFilter, setVendorFilter] = useState<'AUTONOMICS' | 'GAUSIUM'>('AUTONOMICS')

  const [createDialog, setCreateDialog] = useState(false)
  const [createName, setCreateName] = useState('')

  const [isLaunchModalOpen, setIsLaunchModalOpen] = useState(false)
  const [launchItem, setLaunchItem] = useState<{ scenario?: any } | undefined>(undefined)

  // Map state
  const mapRef = useRef<IMapboxElement>(null)
  const [map, setMap] = useState<MapboxMap | undefined>(undefined)
  const [highlightedScenarioId, setHighlightedScenarioId] = useState<number | undefined>(undefined)
  const [gausiumPreviewDismissed, setGausiumPreviewDismissed] = useState(false)
  const mapWrapperRef = useRef<HTMLDivElement>(null)
  const [selectedMissionsDetails, setSelectedMissionsDetails] = useState<IMission[]>([])

  loadingProgressRef.current = loadingProgress

  const onMapInited = () => {
    const m = mapRef.current?.getMap()
    if (m) setMap(m)
  }

  // Fetch full mission geodata for all missions in the selected scenario
  useEffect(() => {
    if (!highlightedScenarioId) {
      setSelectedMissionsDetails([])
      return
    }
    const scenario = scenarios.find(s => s.id === highlightedScenarioId)
    if (scenario?.is_gausium_map) {
      setSelectedMissionsDetails([])
      return
    }
    if (!scenario || !scenario.missions_ids?.length) {
      setSelectedMissionsDetails([])
      return
    }
    let cancelled = false
    const run = async () => {
      try {
        const missionIds = (scenario.missions_ids || []).filter((id: any) => typeof id === 'number')
        const results = await Promise.all(missionIds.map((id: number) => MainApi.fetchMission(id)))
        if (!cancelled) setSelectedMissionsDetails(results)
      } catch (error) {
        console.error('Failed to fetch mission details for scenario', error)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [highlightedScenarioId, scenarios])

  const selectedGausiumPreview = useMemo(() => {
    if (!highlightedScenarioId) return null
    const s = scenarios.find(x => x.id === highlightedScenarioId)
    if (!s?.is_gausium_map) return null
    return { url: s.preview_url as string | null | undefined, name: String(s.name || '') }
  }, [highlightedScenarioId, scenarios])

  useEffect(() => {
    setGausiumPreviewDismissed(false)
  }, [highlightedScenarioId])

  // Zoom to all mission geometry of the selected scenario
  useEffect(() => {
    if (map && selectedMissionsDetails.length > 0) {
      const features: Feature[] = selectedMissionsDetails.flatMap(m => [
        ...m.cleaningZones,
        ...m.restrictZones,
        ...m.cleaningRoutes,
        ...m.transferRoutes,
        ...(m.pointRoutes || []),
      ])
      if (features.length > 0) zoomMapTo(map, features)
    }
  }, [map, selectedMissionsDetails])

  const fetchData = useCallback(
    async (forceRefresh = false) => {
      const cacheKey = `${vendorFilter}_${currentPage}_${debouncedSearchQuery}`
      if (!forceRefresh && lastFetchedPage.current === cacheKey) return
      lastFetchedPage.current = cacheKey
      try {
        if (loadingProgressRef.current === PROGRESS_TYPES.SUCCESS) {
          setListRefreshing(true)
        } else {
          setLoadingProgress(PROGRESS_TYPES.WORK)
        }
        const merged: any[] = []
        let count = 0

        if (vendorFilter === 'AUTONOMICS') {
          const auto = await MainApi.fetchScenarios(currentPage, PAGE_SIZE, debouncedSearchQuery || undefined)
          merged.push(...auto.results)
          count = Math.max(count, auto.count)
        }

        if (GAUSIUM_ENABLED && vendorFilter === 'GAUSIUM') {
          const { results: maps, total: gsTotal } = await MainApi.fetchGausiumMaps(
            undefined,
            debouncedSearchQuery || undefined,
            currentPage,
            PAGE_SIZE
          )
          const gsAsScenarios = maps.map(m => {
            const partitions = (m.subareas_json?.partitions as any[]) || []
            const subareas = partitions
              .map((p: any) => String(p?.name ?? '').trim())
              .filter((v: string) => !!v)
            return {
              id: m.id,
              name: m.map_name || '(no name)',
              missions_ids: subareas,
              missions_count: subareas.length,
              missions_statuses: ['ok'],
              robot_type: m.robot_type || '',
              is_gausium_map: true,
              preview_url: m.preview_url ?? null,
            }
          })
          merged.push(...gsAsScenarios)
          count = Math.max(count, gsTotal)
        }

        setScenarios(merged)
        setTotalCount(count)
        setListRefreshing(false)
        setLoadingProgress(PROGRESS_TYPES.SUCCESS)
      } catch (error) {
        setListRefreshing(false)
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          setLoadingProgress(PROGRESS_TYPES.IDLE)
          return
        }
        setLoadingProgress(PROGRESS_TYPES.ERROR)
        console.warn('[ScenarioList] fetchData error:', error)
      }
    },
    [currentPage, debouncedSearchQuery, vendorFilter]
  )

  useEffect(() => {
    fetchData()
  }, [fetchData])
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchQuery])
  useEffect(() => {
    setCurrentPage(1)
  }, [vendorFilter])
  useEffect(() => {
    setPageInput(currentPage.toString())
  }, [currentPage])

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const handlePageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = parseInt(pageInput)
      const maxPage = Math.ceil(totalCount / PAGE_SIZE) || 1
      if (!isNaN(val) && val >= 1 && val <= maxPage) setCurrentPage(val)
      else setPageInput(currentPage.toString())
    }
  }

  const createScenario = async () => {
    try {
      setCreatingProgress(PROGRESS_TYPES.WORK)
      const scenario = await MainApi.createScenario(createName.trim())
      setCreatingProgress(PROGRESS_TYPES.SUCCESS)
      setCreateDialog(false)
      setCreateName('')
      navigate(`${PATH.SCENARIOS}${scenario.id}`)
    } catch (error) {
      setCreatingProgress(PROGRESS_TYPES.ERROR)
      console.error('Error creating scenario:', error)
    }
  }

  return (
    <div className={style.container}>
      <div className={style.titleBlock}>
        <Title text="Scenarios" />
        <Button.Standart
          onClick={() => {
            setCreateName('')
            setCreateDialog(true)
          }}
          loading={creatingProgress === PROGRESS_TYPES.WORK}
        >
          Add a Scenario
        </Button.Standart>
      </div>
      <div className={style.controlsBlock}>
        <select
          className={style.searchInput}
          value={vendorFilter}
          onChange={e => setVendorFilter(e.target.value as 'AUTONOMICS' | 'GAUSIUM')}
          style={{ maxWidth: 220 }}
        >
          <option value="AUTONOMICS">Autonomics</option>
          {GAUSIUM_ENABLED && <option value="GAUSIUM">Gausium</option>}
        </select>
        <input
          className={style.searchInput}
          type="text"
          placeholder="Search scenarios..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      <div className={style.main}>
        {loadingProgress === PROGRESS_TYPES.WORK && <Loader />}
        {loadingProgress === PROGRESS_TYPES.ERROR && <div>Error</div>}
        {loadingProgress === PROGRESS_TYPES.SUCCESS && (
          <>
            <Box className={style.box}>
              <div className={style.tableWrapper}>
                {listRefreshing && (
                  <div className={style.tableLoadingOverlay} aria-busy="true" aria-live="polite">
                    <Loader />
                  </div>
                )}
                <ScenarioTable
                  scenarios={scenarios}
                  highlightedScenarioId={highlightedScenarioId}
                  onScenarioClick={id => setHighlightedScenarioId(id)}
                  onLaunchScenario={scenario => {
                    if (scenario.is_gausium_map) return
                    setLaunchItem({ scenario })
                    setIsLaunchModalOpen(true)
                  }}
                  onScenariosUpdated={() => fetchData(true)}
                />
                {totalCount > 0 && (
                  <div className={style.pagination}>
                    <button
                      className={style.paginationButton}
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      &larr;
                    </button>
                    <span className={style.pageInfo}>
                      <input
                        className={style.pageInput}
                        value={pageInput}
                        onChange={e => setPageInput(e.target.value)}
                        onKeyDown={handlePageJump}
                        onBlur={() => setPageInput(currentPage.toString())}
                      />
                      / {Math.ceil(totalCount / PAGE_SIZE) || 1}
                    </span>
                    <button
                      className={style.paginationButton}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      disabled={currentPage * PAGE_SIZE >= totalCount}
                    >
                      &rarr;
                    </button>
                  </div>
                )}
              </div>
            </Box>

            {/* Map always rendered so Mapbox initialises */}
            <div className={style.mapWrapper} ref={mapWrapperRef}>
              <MapboxCommon ref={mapRef} disableRuler={true} overlayItems={[]} onMapInited={onMapInited}>
                {selectedMissionsDetails.length > 0 && (
                  <>
                    <RestrictZonesOnMap restrictZones={selectedMissionsDetails.flatMap(m => m.restrictZones)} />
                    <CleaningZonesOnMap cleaningZones={selectedMissionsDetails.flatMap(m => m.cleaningZones)} />
                    <CleaningRoutesOnMap cleaningRoutes={selectedMissionsDetails.flatMap(m => m.cleaningRoutes)} />
                    <TransferRoutesOnMap
                      transferRoutes={selectedMissionsDetails.flatMap(m => [
                        ...m.transferRoutes,
                        ...(m.pointRoutes || []),
                      ])}
                    />
                    {selectedMissionsDetails.map(m => (
                      <CleaningPointsOnMap
                        key={'pts-' + m.mission.id}
                        cleaningRoutes={m.cleaningRoutes}
                        launchPoints={m.launchPoints}
                      />
                    ))}
                    <MissionStartFinishPoints
                      startPoints={selectedMissionsDetails.map(m => m.startingPoint)}
                      finishPoints={selectedMissionsDetails.map(m => m.finishingPoint)}
                    />
                  </>
                )}
              </MapboxCommon>
              <GausiumMapPreviewOverlay
                visible={!!selectedGausiumPreview && !gausiumPreviewDismissed}
                previewUrl={selectedGausiumPreview?.url}
                mapName={selectedGausiumPreview?.name}
                mapWidgetRootRef={mapWrapperRef}
                onClose={() => setGausiumPreviewDismissed(true)}
              />
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        title="Create a new scenario"
        isOpen={createDialog}
        onClose={() => setCreateDialog(false)}
        onConfirmClick={createScenario}
        confirmText="Create"
        confirmButtonDisabled={!createName.trim()}
      >
        <div>
          <TextFieldDescribed
            description={undefined}
            placeholder="Scenario name"
            value={createName}
            onChange={e => setCreateName(e.target.value)}
          />
        </div>
      </ConfirmDialog>

      <TaskSettingsModal
        isOpen={isLaunchModalOpen}
        taskName=""
        onClose={() => {
          setIsLaunchModalOpen(false)
          setLaunchItem(undefined)
        }}
        onSubmit={() => {
          setIsLaunchModalOpen(false)
          setLaunchItem(undefined)
          fetchData(true)
        }}
        taskToEdit={undefined}
        dateToEdit={null}
        initialLaunchItem={launchItem}
      />
    </div>
  )
}

export default ScenarioList
